import React from 'react';
import ReactDOM from 'react-dom';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import './AlertHelpModal.css';

/**
 * AlertHelpModal Component - Modal showing alert threshold information
 * Displays static help text explaining alert types, thresholds, and lifecycle
 * Uses ReactDOM to render in a portal for proper modal behavior
 */
function AlertHelpModal({ isOpen, onClose }) {
  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const content = (
    <div className="alert-help-modal-overlay" onClick={onClose}>
      <div className="alert-help-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="alert-help-modal-header">
          <h2>Alert Thresholds & Help</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        {/* Content */}
        <div className="alert-help-modal-body">
          {/* Alert Types & Thresholds */}
          <section className="alert-help-section">
            <h3>Alert Types & Thresholds</h3>
            <table className="alert-help-table">
              <thead>
                <tr>
                  <th>Alert Type</th>
                  <th>Severity</th>
                  <th>Condition</th>
                  <th>Threshold</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td rowSpan="2"><strong>PPM (Preventive Maintenance)</strong></td>
                  <td>🔴 Critical</td>
                  <td>Overdue</td>
                  <td>Past due date</td>
                </tr>
                <tr>
                  <td>⚠️ Warning</td>
                  <td>Due Soon</td>
                  <td>Within 30 days</td>
                </tr>
                <tr>
                  <td rowSpan="2"><strong>Calibration</strong></td>
                  <td>🔴 Critical</td>
                  <td>Overdue</td>
                  <td>Past due date</td>
                </tr>
                <tr>
                  <td>⚠️ Warning</td>
                  <td>Due Soon</td>
                  <td>Within 30 days</td>
                </tr>
                <tr>
                  <td rowSpan="2"><strong>Warranty</strong></td>
                  <td>🔴 Critical</td>
                  <td>Expired</td>
                  <td>Past expiry date</td>
                </tr>
                <tr>
                  <td>⚠️ Warning</td>
                  <td>Expiring Soon</td>
                  <td>Within 30 days</td>
                </tr>
                <tr>
                  <td rowSpan="2"><strong>Service Contract (LAMC/CAMC)</strong></td>
                  <td>🔴 Critical</td>
                  <td>Expired</td>
                  <td>Past expiry date</td>
                </tr>
                <tr>
                  <td>⚠️ Warning</td>
                  <td>Expiring Soon</td>
                  <td>Within 30 days</td>
                </tr>
                <tr>
                  <td rowSpan="2"><strong>Regulatory Compliance</strong></td>
                  <td>🔴 Critical</td>
                  <td>Overdue</td>
                  <td>Past due date</td>
                </tr>
                <tr>
                  <td>⚠️ Warning</td>
                  <td>Due Soon</td>
                  <td>Within 30 days</td>
                </tr>
                <tr>
                  <td><strong>High Cost Pending</strong></td>
                  <td>🔴 Critical</td>
                  <td>High Cost Device</td>
                  <td>Device cost above location high-cost approval threshold (pending approval)</td>
                </tr>
                <tr>
                  <td><strong>Budget Overrun</strong></td>
                  <td>🔴 Critical</td>
                  <td>Overrun</td>
                  <td>Annual device budget exceeded</td>
                </tr>
                <tr>
                  <td><strong>Device Submitted for Approval</strong></td>
                  <td>⚠️ Warning</td>
                  <td>Pending Review</td>
                  <td>Device submitted by manager awaiting admin approval</td>
                </tr>
                <tr>
                  <td><strong>Device Approved</strong></td>
                  <td>ℹ️ Info</td>
                  <td>Approval Complete</td>
                  <td>Device has been approved by admin</td>
                </tr>
                <tr>
                  <td><strong>Device Rejected</strong></td>
                  <td>⚠️ Warning</td>
                  <td>Rejection</td>
                  <td>Device rejected by admin - resubmission required</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Alert Lifecycle */}
          <section className="alert-help-section">
            <h3>Alert Lifecycle</h3>
            <table className="alert-help-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Active</strong></td>
                  <td>Alert is newly created or reopened - requires attention</td>
                </tr>
                <tr>
                  <td><strong>Acknowledged</strong></td>
                  <td>Alert has been reviewed by user - action planned or in progress</td>
                </tr>
                <tr>
                  <td><strong>Snoozed</strong></td>
                  <td>Alert is temporarily hidden until snooze period ends</td>
                </tr>
                <tr>
                  <td><strong>Archived</strong></td>
                  <td>Alert is resolved and hidden from active view</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Key Information */}
          <section className="alert-help-section">
            <h3>Key Information</h3>
            <ul>
              <li><strong>All alerts are pre-calculated:</strong> Alerts are generated and stored in the database based on device data and configured thresholds</li>
              <li><strong>Status persists:</strong> Changes to alert status (acknowledge, snooze, archive) are immediately saved and persist across sessions</li>
              <li><strong>Auto-resolution:</strong> Maintenance alerts automatically resolve when the condition is cleared (e.g., maintenance completed)</li>
              <li><strong>Role-based visibility:</strong> You only see alerts for devices in your location/department based on your role</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );

  // Use ReactDOM Portal to render at root level
  return ReactDOM.createPortal(content, document.body);
}

export default AlertHelpModal;
